// The bridge: Giraffe state -> provider -> store. One hook, mounted once.

import { useEffect, useRef, useState } from 'react';
import { store } from '@/ui/store';
import { LiveGiraffeData } from './provider';
import {
  getProjectUsages,
  inGiraffe,
  onState,
  readProjectUsages,
  readState,
  readUntypedState,
  startListening,
  type StateKey,
} from './adapter';

export type BridgeStatus = 'offline' | 'connecting' | 'connected';

const WATCHED: StateKey[] = [
  'bakedSections',
  'rawSections',
  'project',
  'selected',
  'projectLayers',
  'layerTree',
  'selectedProjectApp',
  'projectAppsByAppID',
];

export function useGiraffe(enabled: boolean): { status: BridgeStatus; data: LiveGiraffeData | null } {
  const [status, setStatus] = useState<BridgeStatus>(enabled ? 'connecting' : 'offline');
  const dataRef = useRef<LiveGiraffeData | null>(null);
  const connectedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    const data = new LiveGiraffeData();
    dataRef.current = data;
    store.setDataProvider(data);
    startListening();

    /**
     * The bridge answers empty BEFORE the handshake completes — indistinguishable
     * from a genuinely empty project. So "connected" is only claimed once a read
     * actually carries something, and until then the app says "connecting".
     */
    /*
     * Usages are compared by content, not by reference.
     *
     * `projectAppsByAppID` broadcasts on every write to any project app — this
     * app's own 60-second save included — and the object identity changes each
     * time. Rebuilding the sheet on all of them is churn nobody asked for.
     */
    let lastUsages = '';
    const pullUsagesFromState = (): boolean => {
      const usages = readProjectUsages();
      if (usages === null) return false;
      const json = JSON.stringify(usages);
      if (json === lastUsages) return false;
      lastUsages = json;
      return data.setSource('usages', usages);
    };

    const pull = (): void => {
      let changed = false;
      changed = pullUsagesFromState() || changed;
      changed = data.setSource('bakedSections', readState('bakedSections')) || changed;
      changed = data.setSource('rawSections', readState('rawSections')) || changed;
      // Selection is kept apart: no system sheet shows it, only GI.* formulas
      // read it, so a click recalculates those and rewrites nothing.
      const selectionChanged = data.setSelection(readUntypedState('lensSelection'), readState('selected'));
      // Which drawing layers exist — the Features sheet offers them as the
      // layerId column's list, so a feature can be moved between them.
      changed = data.setLayerTree(readState('layerTree')) || changed;
      const project = readState('project');
      changed = data.setProject(project) || changed;
      /*
       * Metric or imperial. The project's numbers are metric either way; this
       * decides which units the sheet SPEAKS (data/units.ts). It goes to the
       * store rather than the provider because the workbook can override it,
       * and the store owns the document.
       *
       * Guarded inside `setProjectUnits` rather than here: Giraffe rebroadcasts
       * `project` as a fresh object constantly, and re-unitising the sheets on
       * every broadcast would be churn nobody asked for.
       */
      store.setProjectUnits(project);
      if (!connectedRef.current && data.rows('bakedSections').length > 0) {
        connectedRef.current = true;
        setStatus('connected');
      }
      if (changed) store.notifyDataChanged();
      else if (selectionChanged) store.notifySelectionChanged();
    };

    /*
     * Debounced: a bake or a layer change arrives as a burst of broadcasts,
     * and each changed one used to queue its own full rewrite and recalc. The
     * model is read only once it has been still for 200 ms. Until the first
     * real data has landed there is nothing to protect, so that goes through
     * at once.
     */
    let settle: ReturnType<typeof setTimeout> | undefined;
    const off = onState(WATCHED, () => {
      clearTimeout(settle);
      if (!connectedRef.current) {
        pull();
        return;
      }
      settle = setTimeout(pull, 200);
    });
    pull();

    // The refetcher is what this app's own writes call to read the project back
    // before redrawing the sheet — a delete that is not re-read comes straight
    // back on the next redraw and looks as though it failed.
    let cancelled = false;
    const pullUsages = async (): Promise<void> => {
      // The state read is the live path; this is the fallback for the window
      // before the app state is readable, and the re-read after our own write.
      if (pullUsagesFromState()) {
        store.notifyDataChanged();
        return;
      }
      const usages = await getProjectUsages();
      if (cancelled || !usages) return;
      lastUsages = JSON.stringify(usages);
      if (data.setSource('usages', usages)) store.notifyDataChanged();
    };
    void pullUsages();
    store.setRefetcher(pullUsages);

    // Retry with backoff: the first reads race the handshake and come back empty.
    const timers = [400, 1000, 2500, 5000].map((ms) =>
      setTimeout(() => {
        pull();
        void pullUsages();
      }, ms)
    );

    return () => {
      cancelled = true;
      store.setRefetcher(null);
      off();
      clearTimeout(settle);
      for (const t of timers) clearTimeout(t);
    };
  }, [enabled]);

  return { status, data: dataRef.current };
}

export function shouldConnect(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.has('demo')) return false;
  return inGiraffe();
}
